/**
 * Detailansicht eines Rezepts: Zeiten, Zutaten, Schritte, Bewertungen und
 * die Einstiege in den Kochmodus und den Editor.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatMinutes, hasParallelSteps, planSchedule, stepSegments, timeSegments } from '@cookbook/domain';
import { ApiError, api } from '../api/client.ts';
import type { RecipeDetail } from '../api/types.ts';
import { useQuery } from '../api/useQuery.ts';
import { sendOrQueue } from '../state/outbox.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { DifficultyBadge } from '../components/DifficultyBadge.tsx';
import { FavoriteButton } from '../components/FavoriteButton.tsx';
import { IngredientList } from '../components/IngredientList.tsx';
import { NutritionPanel } from '../components/NutritionPanel.tsx';
import { PdfMenu } from '../components/PdfMenu.tsx';
import { OfflineVideosButton } from '../components/OfflineVideosButton.tsx';
import { ScaleControl, yieldLabel } from '../components/ScaleControl.tsx';
import { AddToShoppingDialog } from '../components/shopping/AddToShoppingDialog.tsx';
import { StepAnimation } from '../components/StepAnimation.tsx';
import { StepMedia } from '../components/StepMedia.tsx';
import { SchedulePlan } from '../components/SchedulePlan.tsx';
import { StepText } from '../components/StepText.tsx';
import { TimeBar } from '../components/TimeBar.tsx';
import { printPage } from '../lib/print.ts';
import { useScaling } from '../state/useScaling.ts';
import { Stars } from '../components/Stars.tsx';
import { texts } from '../texts/de.ts';

/** Detailseite eines Rezepts. */
export function RecipeDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useQuery<{ recipe: RecipeDetail }>(
    id === '' ? null : `/api/recipes/${id}`,
  );
  const [comment, setComment] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [shoppingOpen, setShoppingOpen] = useState(false);
  // Hält die Merkliste auch ohne Netz richtig, bis der Server sie bestätigt.
  const [favoriteOverride, setFavoriteOverride] = useState<boolean | null>(null);

  const recipe = data?.recipe ?? null;
  const scaling = useScaling(recipe);

  /** Setzt die eigene Bewertung. */
  async function rate(stars: number) {
    if (recipe === null) {
      return;
    }
    setMessage(null);
    try {
      const result = await sendOrQueue({
        key: `rating:${recipe.id}`,
        method: 'PUT',
        path: `/api/recipes/${recipe.id}/rating`,
        body: { stars, comment: comment === '' ? null : comment },
        label: `${texts.detail.myRating}: ${recipe.title} (${stars})`,
      });
      reload();
      setMessage(result.queued ? texts.sync.savedOffline : texts.detail.ratingSaved);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  /** Nimmt die eigene Bewertung zurück. */
  async function removeRating() {
    if (recipe === null) {
      return;
    }
    const result = await sendOrQueue({
      key: `rating:${recipe.id}`,
      method: 'DELETE',
      path: `/api/recipes/${recipe.id}/rating`,
      body: null,
      label: `${texts.detail.removeRating}: ${recipe.title}`,
    });
    reload();
    if (result.queued) {
      setMessage(texts.sync.savedOffline);
    }
  }

  /** Löscht das Rezept nach Rückfrage. */
  async function remove() {
    if (recipe === null || !window.confirm(texts.detail.deleteConfirm)) {
      return;
    }
    try {
      await api.delete(`/api/recipes/${recipe.id}`);
      void navigate('/rezepte');
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  if (loading) {
    return <p className="px-4 py-6 text-sm text-content-muted">{texts.common.loading}</p>;
  }
  if (error !== null || recipe === null) {
    return (
      <div className="px-4 py-6">
        <p className="rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
          {error ?? 'Das Rezept gibt es nicht.'}
        </p>
        <Link to="/rezepte" className="mt-3 inline-block text-sm text-accent underline">
          {texts.common.back}
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-3xl px-4 py-6">
      {shoppingOpen ? (
        <AddToShoppingDialog recipe={recipe} factor={scaling.factor} onClose={() => setShoppingOpen(false)} />
      ) : null}
      <Link
        to="/rezepte"
        className="mb-3 inline-flex items-center gap-1 text-sm text-content-muted print:hidden"
      >
        <AppIcon name="book" size={16} />
        {texts.recipes.title}
      </Link>

      <header className="mb-5">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{recipe.title}</h1>
          <FavoriteButton
            className="print:hidden"
            recipeId={recipe.id}
            title={recipe.title}
            favorite={favoriteOverride ?? recipe.isFavorite}
            size={28}
            onChange={(value) => {
              setFavoriteOverride(value);
              reload();
            }}
          />
        </div>
        {recipe.description === null ? null : <p className="mt-1 text-content-muted">{recipe.description}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-content-muted">
          <Stars value={recipe.ratingAverage} count={recipe.ratingCount} />
          <DifficultyBadge difficulty={recipe.difficulty} withHint />
          <span className="inline-flex items-center gap-1">
            <AppIcon name="clock" size={16} />
            {formatMinutes(recipe.totalMinutes)} gesamt
          </span>
          <span className="inline-flex items-center gap-1">
            <AppIcon name="flame" size={16} />
            {formatMinutes(recipe.activeMinutes)} aktiv
          </span>
          <span>
            {recipe.baseAmount} {yieldLabel(recipe, recipe.baseAmount)}
            {recipe.baseUnitDetail === null ? '' : ` · ${recipe.baseUnitDetail}`}
          </span>
        </div>
        <p className="mt-2 text-xs text-content-muted">
          {texts.detail.createdBy} {recipe.createdByName} ·{' '}
          {recipe.lastCookedAt === null
            ? texts.detail.neverCooked
            : `${texts.detail.lastCooked}: ${new Date(recipe.lastCookedAt).toLocaleDateString('de-DE')}`}
        </p>
      </header>

      {recipe.media.length === 0 ? null : (
        <section className="mb-5">
          {recipe.coverImageUrl === null ? null : (
            <img
              src={recipe.coverImageUrl.replace('/thumb', '/file')}
              alt=""
              className="mb-3 max-h-96 w-full rounded-card object-cover print:max-h-64"
            />
          )}
          <div className="mb-2 print:hidden">
            <OfflineVideosButton
              urls={[...recipe.media, ...recipe.steps.flatMap((step) => step.media ?? [])]
                .filter((item) => item.kind === 'video')
                .map((item) => item.url)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 print:hidden">
            {recipe.media.map((item) =>
              item.kind === 'video' ? (
                <video
                  key={item.id}
                  controls
                  preload="metadata"
                  poster={item.thumbUrl}
                  className="h-40 w-full rounded-xl bg-black object-cover"
                >
                  <source src={item.url} />
                </video>
              ) : (
                <a key={item.id} href={item.url} target="_blank" rel="noreferrer">
                  <img
                    src={item.thumbUrl}
                    alt=""
                    loading="lazy"
                    className="h-40 w-full rounded-xl object-cover"
                  />
                </a>
              ),
            )}
          </div>
        </section>
      )}

      <div className="mb-5 flex flex-wrap gap-2 print:hidden">
        <Link
          to={`/rezepte/${recipe.id}/kochen${scaling.search}`}
          className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent"
        >
          <AppIcon name="flame" size={18} />
          {texts.detail.cookMode}
        </Link>
        <Link
          to={`/rezepte/${recipe.id}/bearbeiten`}
          className="inline-flex items-center gap-2 rounded-xl border border-border-subtle px-4 py-2.5 text-sm"
        >
          <AppIcon name="sliders" size={18} />
          {texts.detail.edit}
        </Link>
        <button
          type="button"
          onClick={() => void remove()}
          className="inline-flex items-center gap-2 rounded-xl border border-border-subtle px-4 py-2.5 text-sm text-content-muted"
        >
          {texts.detail.delete}
        </button>
        <PdfMenu recipeId={recipe.id} search={scaling.search} />
        <button
          type="button"
          onClick={printPage}
          className="inline-flex items-center gap-2 rounded-xl border border-border-subtle px-4 py-2.5 text-sm"
        >
          {texts.print.print}
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-card border border-border-subtle bg-surface-raised p-4">
          <h2 className="mb-2 font-semibold">{texts.detail.times}</h2>
          <TimeBar segments={timeSegments(recipe.times)} title={texts.scale.timeBar} />
          <div className="mt-3 flex items-center justify-between border-t border-border-subtle pt-2 text-sm font-semibold">
            <span>{texts.detail.total}</span>
            <span>{formatMinutes(recipe.totalMinutes)}</span>
          </div>
        </section>

        {recipe.equipment.length === 0 ? null : (
          <section className="rounded-card border border-border-subtle bg-surface-raised p-4">
            <h2 className="mb-2 font-semibold">{texts.detail.equipment}</h2>
            <ul className="space-y-1 text-sm">
              {recipe.equipment.map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <AppIcon name="check" size={16} className="text-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <section className="mt-4 rounded-card border border-border-subtle bg-surface-raised p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">
            {texts.detail.ingredients} ({recipe.ingredients.length})
          </h2>
          {recipe.ingredients.length === 0 ? null : (
            <button
              type="button"
              onClick={() => setShoppingOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border-subtle px-3 py-1.5 text-sm print:hidden"
            >
              <AppIcon name="cart" size={16} />
              {texts.shopping.addFromRecipe}
            </button>
          )}
        </div>
        <div className="print:hidden">
          <ScaleControl recipe={recipe} scaling={scaling} />
        </div>
        {/* Auf Papier steht nur die gewählte Menge. */}
        <p className="hidden text-sm print:block">
          {texts.print.amount
            .replace('{amount}', String(scaling.amount))
            .replace('{unit}', yieldLabel(recipe, scaling.amount))}
          {scaling.pan === null ? '' : ` · ${texts.scale.pan}`}
        </p>
        <div className="mt-2">
          <IngredientList
            ingredients={recipe.ingredients}
            factor={scaling.factor}
            unitMode={scaling.unitMode}
          />
        </div>
      </section>

      <div className="mt-4">
        <NutritionPanel
          ingredients={recipe.ingredients}
          baseUnit={recipe.baseUnit}
          factor={scaling.factor}
          amount={scaling.amount}
        />
      </div>

      <section className="mt-4">
        <h2 className="mb-2 font-semibold">{texts.detail.steps}</h2>
        {hasParallelSteps(recipe.steps) ? (
          <div className="mb-3 rounded-card border border-border-subtle bg-surface-raised p-4 print:break-inside-avoid">
            <SchedulePlan schedule={planSchedule(recipe.steps)} />
          </div>
        ) : stepSegments(recipe.steps).length < 2 ? null : (
          <div className="mb-3 rounded-card border border-border-subtle bg-surface-raised p-4 print:hidden">
            <TimeBar segments={stepSegments(recipe.steps)} title={texts.scale.flow} />
          </div>
        )}
        <ol className="space-y-3">
          {recipe.steps.map((step, index) => (
            <li
              key={step.id}
              className="rounded-card border border-border-subtle bg-surface-raised p-4 print:break-inside-avoid"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft font-semibold text-accent-strong">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  {step.animationTemplateId === null ? null : (
                    <StepAnimation
                      templateId={step.animationTemplateId}
                      params={step.animationParams}
                      className="mb-3 w-full sm:float-right sm:mb-2 sm:ml-4 sm:w-56 print:w-40"
                    />
                  )}
                  {step.title === '' ? null : <p className="font-semibold">{step.title}</p>}
                  <StepText
                    text={step.instruction}
                    ingredients={recipe.ingredients}
                    factor={scaling.factor}
                    unitMode={scaling.unitMode}
                  />
                  <StepMedia media={step.media} className="mt-2 clear-both sm:clear-none" />
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-content-muted">
                    {step.durationMinutes === null ? null : (
                      <span className="inline-flex items-center gap-1">
                        <AppIcon name="clock" size={14} />
                        {step.durationMinutes} {texts.detail.minutesShort}
                        {step.passive ? ` · ${texts.detail.passiveHint}` : ''}
                      </span>
                    )}
                    {step.temperatureCelsius === null ? null : (
                      <span className="inline-flex items-center gap-1">
                        <AppIcon name="flame" size={14} />
                        {step.temperatureCelsius} °C
                      </span>
                    )}
                    {step.timer ? <span>Timer</span> : null}
                  </div>
                  {step.whyItWorks === null ? null : (
                    <p className="mt-2 rounded-xl bg-surface-sunken p-2 text-xs">
                      <strong>{texts.cook.whyItWorks}:</strong> {step.whyItWorks}
                    </p>
                  )}
                  {step.doneWhen === null ? null : (
                    <p className="mt-2 rounded-xl bg-surface-sunken p-2 text-xs">
                      <strong>{texts.cook.doneWhen}</strong> {step.doneWhen}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-6 rounded-card border border-border-subtle bg-surface-raised p-4 print:hidden">
        <h2 className="mb-2 font-semibold">{texts.detail.myRating}</h2>
        <Stars value={recipe.myStars} editable size={28} onChange={(stars) => void rate(stars)} />
        {recipe.myStars === null ? null : (
          <button
            type="button"
            onClick={() => void removeRating()}
            className="ml-3 text-xs text-accent underline"
          >
            {texts.detail.removeRating}
          </button>
        )}
        <textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={texts.detail.commentPlaceholder}
          rows={2}
          className="mt-3 w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        />
        {message === null ? null : <p className="mt-1 text-xs text-accent-strong">{message}</p>}

        <h3 className="mt-4 mb-1 text-sm font-semibold">{texts.detail.ratings}</h3>
        {recipe.ratings.length === 0 ? (
          <p className="text-sm text-content-muted">{texts.detail.noRatings}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {recipe.ratings.map((rating) => (
              <li key={rating.userId} className="rounded-xl bg-surface-sunken p-2">
                <div className="flex items-center gap-2">
                  <strong>{rating.displayName}</strong>
                  <Stars value={rating.stars} size={14} />
                </div>
                {rating.comment === null ? null : <p className="text-content-muted">{rating.comment}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}
